import {Presenter} from '../../Support/with_presenter';
import {WorkoutsService} from '../../Network/WorkoutsService';
import {loadingResponse, NetworkResult} from '../../Network/NetworkResult';
import {PartialWorkout} from '../../Models/PartialWorkout';
import {endOfMonth, startOfYear, subYears} from 'date-fns';
import {DateRange} from '../../Domain/DateRange';
import {CrewWorkoutsTableFilter} from './Components/CrewWorkoutsTableFilter';
import {runInAction, action} from 'mobx';
import {FullCrew} from '../../Models/Crew';

export class CrewsWorkoutIndexPresenter implements Presenter {
    public result: NetworkResult<PartialWorkout[]> = loadingResponse();
    public referenceDate = new Date();

    private crewIds: number[];
    public crews: FullCrew[]; // Store crews
    public userList: Record<number, string> = {};


    public filter: CrewWorkoutsTableFilter;

    public initialDateRange: DateRange = {
        from: startOfYear(subYears(this.referenceDate, 5)),
        till: endOfMonth(this.referenceDate)
    }

    public initialFilter: CrewWorkoutsTableFilter = {
        page: 1,
        dateRange: this.initialDateRange,
    }

    constructor(
        private workoutService: WorkoutsService,
        crewIds: number[],
        crews: FullCrew[],
    ) {
        this.crewIds = crewIds;
        this.crews = crews;
        this.filter = this.initialFilter;
        this.extractUserList();
    }

    private extractUserList(): void {
        const usersMap: Record<number, string> = {};

        this.crews.forEach((crew) => {
            if (crew.members) {
                crew.members.forEach((member) => {
                    if (member.userId !== null) {
                        usersMap[member.userId] = `${member.firstName} ${member.lastName}`;
                    }
                });
            }
        });

        this.userList = usersMap; 
    }

    public setInitialFilter(initialFilter: CrewWorkoutsTableFilter) {
        if (!this.filter) {
            this.filter = this.initialFilter;
            this.fetchWorkouts();
        }
    }

    private async fetchWorkouts(): Promise<void> {
        const dateRange = this.filter.dateRange ?? this.initialDateRange;
        runInAction(() => {
            this.result = loadingResponse();  
        });
        this.result = loadingResponse(); 
        const response = await this.workoutService.forCrews(
            this.crewIds,
            dateRange.from ?? new Date(),
            dateRange.till ?? new Date(),
            this.filter.page ?? 1,
            100,
        );
        runInAction(() => {
            this.result = response;  
        });
    }

    public async updateCrewIds(crewIds: number[]): Promise<void> {
        this.crewIds = crewIds;
        await this.fetchWorkouts();
    }

    public updateDateRange (range: DateRange | undefined): void {
        if (this.filter.dateRange && range && range.from ) {
            this.filter.dateRange.from = range.from
        }
        if (this.filter.dateRange && range && range.till) {
            this.filter.dateRange.till = range.till
        }
        this.fetchWorkouts();
    }

    public previousPage(): void {
    if (this.filter.page > 1) {
        this.filter.page -= 1;
        this.fetchWorkouts();
    }
}

    public nextPage(): void {
        this.filter.page += 1;
        this.fetchWorkouts();
    }

    public onPageChange(page: number): void {
        this.filter.page = page.valueOf();
        this.fetchWorkouts();
    }

    public resetFilter(): void {
        this.filter = this.initialFilter;
        this.fetchWorkouts();
    }

    // Getter methods for external access (optional)
    public getCrewIds(): number[] {
        return [...this.crewIds]; // Return a copy to prevent direct manipulation
    }

    public async mount(): Promise<void> {
        // Initial fetch with default values
        await this.fetchWorkouts();
    }

    public unmount(): void {
        /* Noop */
    }
}